import * as Switch from '@radix-ui/react-switch';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';

import { ApiError, apiFetch, violationFieldErrors, violationMessage } from '../../lib/api';
import { AuthEndorsement, AuthWordmark } from '../../lib/auth-brand';
import { AuthPageShell } from '../../lib/auth-page-shell';
import { useAuth } from '../../lib/auth';
import { DeclarationModal } from '../components/DeclarationModal';
import { ParentShell } from '../components/ParentShell';
import { learnerNavFromSummary, fetchParentProfile } from '../lib/parent-portal-api';
import {
  applyParentProfileDefaults,
  draftPatchFromForm,
  EMPTY_FORM,
  fetchActiveDeclaration,
  fetchMyLearners,
  fetchPartnerSchools,
  fetchRegistrationDraft,
  fetchRegistrationLevels,
  findRegistrationPendingPayment,
  formatLevelLabel,
  formStateFromDraft,
  hasParentProfilePrefill,
  patchRegistrationDraft,
  registrationPaymentPath,
  RELATIONSHIP_OPTIONS,
  SCHOOL_HOME,
  SCHOOL_OTHER,
  submitRegistration,
  RegistrationStepError,
  type RegistrationFormState,
} from '../lib/registration-api';
import { pathForApplication } from '../lib/selected-learner';

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat',
  'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh',
  'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh',
  'Uttarakhand', 'West Bengal', 'Andaman and Nicobar Islands', 'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Jammu and Kashmir', 'Ladakh',
  'Lakshadweep', 'Puducherry',
];

interface OtpRequestResponse {
  requestId: string;
}

interface OtpVerifyResponse {
  accessToken: string;
}

function FieldLabel({
  htmlFor,
  required = true,
  children,
}: {
  htmlFor: string;
  required?: boolean;
  children: ReactNode;
}): ReactElement {
  return (
    <label htmlFor={htmlFor}>
      {children}
      {!required ? <span className="field-optional"> (optional)</span> : null}
    </label>
  );
}

export function RegisterPage(): ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { login, token, loading, me } = useAuth();
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [requestId, setRequestId] = useState<string | null>(null);
  const [form, setForm] = useState<RegistrationFormState>(EMPTY_FORM);
  const [declarationOpen, setDeclarationOpen] = useState(false);
  const [declarationAccepted, setDeclarationAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<string, string>>>({});
  const hydrated = useRef(false);

  const authenticated = Boolean(token);

  const schoolsQuery = useQuery({
    queryKey: ['registration-schools', form.academicLevelId],
    queryFn: () => fetchPartnerSchools(form.academicLevelId),
    enabled: authenticated && Boolean(form.academicLevelId),
  });

  const levelsQuery = useQuery({
    queryKey: ['registration-levels'],
    queryFn: fetchRegistrationLevels,
    enabled: authenticated,
  });

  const draftQuery = useQuery({
    queryKey: ['registration-draft'],
    queryFn: fetchRegistrationDraft,
    enabled: authenticated,
  });

  const declarationQuery = useQuery({
    queryKey: ['registration-declaration'],
    queryFn: () => fetchActiveDeclaration(),
    enabled: authenticated,
  });

  const learnersQuery = useQuery({
    queryKey: ['my-learners'],
    queryFn: fetchMyLearners,
    enabled: authenticated,
  });
  const learners = learnerNavFromSummary(learnersQuery.data ?? []);

  const parentProfileQuery = useQuery({
    queryKey: ['parent-profile'],
    queryFn: fetchParentProfile,
    enabled: authenticated && Boolean(me?.parentGuardianId),
  });

  const draftMutation = useMutation({
    mutationFn: patchRegistrationDraft,
    onSuccess: (data) => {
      queryClient.setQueryData(['registration-draft'], data);
    },
  });

  useEffect(() => {
    if (!authenticated || hydrated.current) return;
    if (draftQuery.isLoading) return;
    if (me?.parentGuardianId && parentProfileQuery.isLoading) return;

    let loaded = draftQuery.data
      ? formStateFromDraft(draftQuery.data.payload)
      : { ...EMPTY_FORM };

    if (loaded.currentSchool === SCHOOL_HOME || loaded.currentSchool === SCHOOL_OTHER) {
      loaded = {
        ...loaded,
        currentSchool: '',
        otherSchoolName: '',
        schoolAdmissionNumber: '',
      };
    }

    if (parentProfileQuery.data) {
      loaded = applyParentProfileDefaults(loaded, parentProfileQuery.data);
    }

    setForm(loaded);
    hydrated.current = true;
  }, [
    authenticated,
    draftQuery.data,
    draftQuery.isLoading,
    me?.parentGuardianId,
    parentProfileQuery.data,
    parentProfileQuery.isLoading,
  ]);

  const autosave = useCallback((next: RegistrationFormState) => {
    if (!authenticated) return;
    draftMutation.mutate(draftPatchFromForm(next));
  }, [authenticated, draftMutation]);

  function blurAutosave(): void {
    setForm((current) => {
      autosave(current);
      return current;
    });
  }

  function updateForm(patch: Partial<RegistrationFormState>, save = false): void {
    setForm((current) => {
      const next = { ...current, ...patch };
      if (save) autosave(next);
      return next;
    });
  }

  async function sendOtp(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const result = await apiFetch<OtpRequestResponse>('/auth/otp/request', {
        method: 'POST',
        body: JSON.stringify({ email, purpose: 'REGISTRATION' }),
      });
      setRequestId(result.requestId);
      setOtp('');
    } catch (err) {
      setError(err instanceof ApiError ? violationMessage(err.problem) : 'Could not send OTP');
    } finally {
      setBusy(false);
    }
  }

  async function requestOtp(event: FormEvent): Promise<void> {
    event.preventDefault();
    await sendOtp();
  }

  async function verifyOtp(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!requestId) return;
    setBusy(true);
    setError(null);
    try {
      const result = await apiFetch<OtpVerifyResponse>('/auth/otp/verify', {
        method: 'POST',
        body: JSON.stringify({ requestId, otp }),
      });
      await login(result.accessToken);
    } catch (err) {
      setError(err instanceof ApiError ? violationMessage(err.problem) : 'Verification failed');
    } finally {
      setBusy(false);
    }
  }

  async function performSubmit(): Promise<void> {
    if (!me?.parentGuardianId || !declarationQuery.data) return;
    setBusy(true);
    setError(null);
    setFieldErrors({});
    try {
      const middle = form.parentMiddleName.trim();
      const acceptedByName = [form.parentFirstName, middle, form.parentLastName]
        .filter(Boolean)
        .join(' ');
      const result = await submitRegistration({
        form,
        parentGuardianId: me.parentGuardianId,
        acceptedByName,
        declaration: declarationQuery.data,
      });
      await queryClient.invalidateQueries({ queryKey: ['registration-draft'] });
      await queryClient.invalidateQueries({ queryKey: ['my-learners'] });
      if (result.shortCircuited) {
        const learners = await fetchMyLearners();
        navigate(pathForApplication(learners, result.applicationId));
        return;
      }
      navigate('/register/payment', {
        state: {
          applicationId: result.applicationId,
          quotationId: result.quotationId,
          payableTotalMinor: result.payableTotalMinor,
        },
      });
    } catch (err) {
      if (err instanceof RegistrationStepError) {
        const cause = err.source;
        if (cause instanceof ApiError) {
          setFieldErrors(violationFieldErrors(cause.problem));
          setError(
            `${violationMessage(cause.problem)} Nothing after "${err.step}" was submitted.`,
          );
        } else if (cause instanceof Error) {
          setError(`${cause.message} Nothing after "${err.step}" was submitted.`);
        } else {
          setError(`Registration failed while saving ${err.step}.`);
        }
        return;
      }
      setError(err instanceof ApiError ? violationMessage(err.problem) : 'Registration failed');
    } finally {
      setBusy(false);
    }
  }

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!declarationAccepted) {
      setDeclarationOpen(true);
      return;
    }
    await performSubmit();
  }

  if (loading) {
    return (
      <main className="shell">
        <p className="text-muted">Loading your session…</p>
      </main>
    );
  }

  if (!authenticated) {
    return (
      <AuthPageShell>
        <AuthWordmark />
        <div className="page-header page-header-centred">
          <h1>Register your child</h1>
          <p className="lede">
            {requestId
              ? `Enter the six-digit code we sent to ${email}.`
              : 'Start with your email address. We will send you a six-digit code.'}
          </p>
        </div>

        <div className="card">
          {error ? (
            <div className="error-banner" role="alert">
              {error}
            </div>
          ) : null}
          {!requestId ? (
            <form onSubmit={requestOtp}>
              <div className="field">
                <FieldLabel htmlFor="email">Email address</FieldLabel>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
                <span className="field-hint">
                  Your progress is saved against this address, so you can leave and come back.
                </span>
              </div>
              <div className="section-gap">
                <button
                  className="btn btn-lg btn-block"
                  type="submit"
                  disabled={busy || !email.includes('@')}
                >
                  {busy ? 'Sending…' : 'Send verification code'}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={verifyOtp}>
              <div className="field">
                <FieldLabel htmlFor="otp">Verification code</FieldLabel>
                <input
                  id="otp"
                  className="otp-input"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={otp}
                  onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  pattern="[0-9]{6}"
                />
              </div>
              <div className="stack section-gap">
                <button
                  className="btn btn-lg btn-block"
                  type="submit"
                  disabled={busy || otp.length !== 6}
                >
                  {busy ? 'Verifying…' : 'Verify and continue'}
                </button>
                <button
                  className="btn btn-ghost btn-block"
                  type="button"
                  disabled={busy}
                  onClick={() => void sendOtp()}
                >
                  {busy ? 'Sending…' : 'Resend code'}
                </button>
                <button
                  className="btn btn-ghost btn-block"
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setRequestId(null);
                    setOtp('');
                    setError(null);
                  }}
                >
                  Use a different email
                </button>
              </div>
            </form>
          )}
        </div>
        <p className="text-sm text-muted text-center section-gap">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
        <AuthEndorsement />
      </AuthPageShell>
    );
  }

  if (draftQuery.isLoading || levelsQuery.isLoading || learnersQuery.isLoading) {
    return (
      <ParentShell learners={learners} title="Add a child">
        <p className="text-muted">Loading your saved details…</p>
      </ParentShell>
    );
  }

  const pendingPayment = findRegistrationPendingPayment(learnersQuery.data ?? []);
  if (pendingPayment) {
    return <Navigate to={registrationPaymentPath(pendingPayment.applicationId)} replace />;
  }

  return (
    <ParentShell learners={learners} title="Add a child">
      <div className="portal-page-wide">
      <ol className="progress-rail">
        <li className="progress-step" data-complete="true">
          <span className="progress-step-dot" aria-hidden="true">
            ✓
          </span>
          <span className="progress-step-label">Verify email</span>
        </li>
        <span className="progress-connector" aria-hidden="true" />
        <li className="progress-step" aria-current="step">
          <span className="progress-step-dot" aria-hidden="true">
            2
          </span>
          <span className="progress-step-label">Learner details</span>
        </li>
        <span className="progress-connector" aria-hidden="true" />
        <li className="progress-step">
          <span className="progress-step-dot" aria-hidden="true">
            3
          </span>
          <span className="progress-step-label">Registration fee</span>
        </li>
      </ol>

      <div className="page-header">
        <p className="lede">
          Tell us about your child and we will create their Learn Plus enrolment. You can
          leave this page and come back — your answers are saved as you type.
        </p>
      </div>

      {error ? (
        <div className="error-banner" role="alert">
          {error}
        </div>
      ) : null}

      <div className="card">
        <form onSubmit={onSubmit} id="registration-form">
          <section className="form-section">
            <div className="form-section-header">
              <h2>Learner details</h2>
              <p>As they appear on the birth certificate or passport.</p>
            </div>
            <div className="field-row field-row-3">
              <div className="field">
                <FieldLabel htmlFor="learnerFirstName">First name</FieldLabel>
                <input
                  id="learnerFirstName"
                  value={form.learnerFirstName}
                  onChange={(event) => updateForm({ learnerFirstName: event.target.value })}
                  onBlur={blurAutosave}
                  required
                />
              </div>
              <div className="field">
                <FieldLabel htmlFor="learnerMiddleName" required={false}>Middle name</FieldLabel>
                <input
                  id="learnerMiddleName"
                  value={form.learnerMiddleName}
                  onChange={(event) => updateForm({ learnerMiddleName: event.target.value })}
                  onBlur={blurAutosave}
                />
              </div>
              <div className="field">
                <FieldLabel htmlFor="learnerLastName">Last name</FieldLabel>
                <input
                  id="learnerLastName"
                  value={form.learnerLastName}
                  onChange={(event) => updateForm({ learnerLastName: event.target.value })}
                  onBlur={blurAutosave}
                  required
                />
              </div>
            </div>
            <div className="field-row field-row-3">
              <div className="field">
                <FieldLabel htmlFor="gender">Gender</FieldLabel>
                <select
                  id="gender"
                  value={form.gender}
                  onChange={(event) => updateForm({ gender: event.target.value }, true)}
                  required
                >
                  <option value="">Select…</option>
                  <option value="MALE">Male</option>
                  <option value="FEMALE">Female</option>
                  <option value="OTHER">Other / prefer not to say</option>
                </select>
              </div>
              <div className="field">
                <FieldLabel htmlFor="dob">Date of birth</FieldLabel>
                <input
                  id="dob"
                  type="date"
                  value={form.dateOfBirth}
                  onChange={(event) => updateForm({ dateOfBirth: event.target.value }, true)}
                  required
                />
              </div>
              <div className="field">
                <FieldLabel htmlFor="academicLevelId">Learner&apos;s current grade</FieldLabel>
                <select
                  id="academicLevelId"
                  value={form.academicLevelId}
                  onChange={(event) => updateForm({
                    academicLevelId: event.target.value,
                    currentSchool: '',
                    otherSchoolName: '',
                    schoolAdmissionNumber: '',
                  }, true)}
                  required
                >
                  <option value="">Select grade…</option>
                  {(levelsQuery.data ?? []).map((level) => (
                    <option key={level.id} value={level.id}>
                      {formatLevelLabel(level)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="field">
              <FieldLabel htmlFor="currentSchool">Learner current school</FieldLabel>
              <select
                id="currentSchool"
                value={form.currentSchool}
                onChange={(event) => updateForm({
                  currentSchool: event.target.value,
                }, true)}
                disabled={!form.academicLevelId || schoolsQuery.isLoading}
                required
              >
                <option value="">
                  {!form.academicLevelId
                    ? 'Select a grade first…'
                    : (schoolsQuery.isLoading ? 'Loading schools…' : 'Select…')}
                </option>
                {(schoolsQuery.data?.items ?? []).map((school) => (
                  <option key={school.id} value={school.id}>
                    {school.name} — {school.city}
                  </option>
                ))}
              </select>
            </div>
            {form.currentSchool ? (
              <div className="field">
                <FieldLabel htmlFor="schoolAdmissionNumber">School admission number</FieldLabel>
                <input
                  id="schoolAdmissionNumber"
                  value={form.schoolAdmissionNumber}
                  onChange={(event) => updateForm({ schoolAdmissionNumber: event.target.value })}
                  onBlur={blurAutosave}
                  autoComplete="off"
                  maxLength={64}
                  required
                />
              </div>
            ) : null}
          </section>

          <section className="form-section">
            <div className="form-section-header">
              <h2>Address</h2>
            </div>
            <div className="field-row field-row-4">
              <div className="field">
                <FieldLabel htmlFor="city">City</FieldLabel>
                <input
                  id="city"
                  value={form.city}
                  onChange={(event) => updateForm({ city: event.target.value })}
                  onBlur={blurAutosave}
                  required
                />
              </div>
              <div className="field">
                <FieldLabel htmlFor="state">State</FieldLabel>
                <select
                  id="state"
                  value={form.state}
                  onChange={(event) => updateForm({ state: event.target.value }, true)}
                  required
                >
                  {INDIAN_STATES.map((item) => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <FieldLabel htmlFor="country">Country</FieldLabel>
                <input id="country" value="India" disabled title="India only at launch" />
              </div>
              <div className="field">
                <FieldLabel htmlFor="pincode">PIN code</FieldLabel>
                <input
                  id="pincode"
                  inputMode="numeric"
                  value={form.pincode}
                  onChange={(event) => updateForm({
                    pincode: event.target.value.replace(/\D/g, '').slice(0, 6),
                  })}
                  onBlur={blurAutosave}
                  required
                  pattern="[1-9][0-9]{5}"
                />
              </div>
            </div>
          </section>

          <section className="form-section">
            <div className="form-section-header">
              <h2>Parent or guardian</h2>
              <p>
                We will use these details for approvals and fee notices.
                {parentProfileQuery.data && hasParentProfilePrefill(parentProfileQuery.data) ? (
                  <> We&apos;ve filled in your saved details — edit anything that differs for this child.</>
                ) : null}
              </p>
            </div>
            <div className="field-row field-row-3">
              <div className="field">
                <FieldLabel htmlFor="parentFirstName">First name</FieldLabel>
                <input
                  id="parentFirstName"
                  value={form.parentFirstName}
                  onChange={(event) => updateForm({ parentFirstName: event.target.value })}
                  onBlur={blurAutosave}
                  required
                />
              </div>
              <div className="field">
                <FieldLabel htmlFor="parentMiddleName" required={false}>Middle name</FieldLabel>
                <input
                  id="parentMiddleName"
                  value={form.parentMiddleName}
                  onChange={(event) => updateForm({ parentMiddleName: event.target.value })}
                  onBlur={blurAutosave}
                />
              </div>
              <div className="field">
                <FieldLabel htmlFor="parentLastName">Last name</FieldLabel>
                <input
                  id="parentLastName"
                  value={form.parentLastName}
                  onChange={(event) => updateForm({ parentLastName: event.target.value })}
                  onBlur={blurAutosave}
                  required
                />
              </div>
            </div>
            <div className="field-row">
              <div className="field">
                <FieldLabel htmlFor="relationshipToLearner">Relationship to learner</FieldLabel>
                <select
                  id="relationshipToLearner"
                  value={form.relationshipToLearner}
                  onChange={(event) => updateForm({
                    relationshipToLearner: event.target.value as RegistrationFormState['relationshipToLearner'],
                  }, true)}
                  required
                >
                  <option value="">Select…</option>
                  {RELATIONSHIP_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="field-row">
              <div className="field">
                <FieldLabel htmlFor="parentMobile">Phone number</FieldLabel>
                <div className="phone-input">
                  <span className="phone-prefix">{form.parentMobileCountryCode}</span>
                  <input
                    id="parentMobile"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    placeholder="9876543210"
                    value={form.parentMobile}
                    onChange={(event) => updateForm({
                      parentMobile: event.target.value.replace(/\D/g, '').slice(0, 10),
                    })}
                    onBlur={blurAutosave}
                    required
                    pattern="[6-9][0-9]{9}"
                    aria-invalid={fieldErrors.mobile ? true : undefined}
                    aria-describedby={fieldErrors.mobile ? 'parentMobile-error' : undefined}
                  />
                </div>
                {fieldErrors.mobile ? (
                  <p id="parentMobile-error" className="field-error" role="alert">{fieldErrors.mobile}</p>
                ) : null}
                <div className="switch-row">
                  <label htmlFor="mobileIsWhatsapp">Is this your WhatsApp number?</label>
                  <Switch.Root
                    id="mobileIsWhatsapp"
                    className="switch-root"
                    checked={form.mobileIsWhatsapp}
                    onCheckedChange={(checked) => updateForm({ mobileIsWhatsapp: checked === true }, true)}
                  >
                    <Switch.Thumb className="switch-thumb" />
                  </Switch.Root>
                </div>
              </div>
              {!form.mobileIsWhatsapp ? (
                <div className="field">
                  <FieldLabel htmlFor="whatsappMobile" required={false}>WhatsApp number</FieldLabel>
                  <div className="phone-input">
                    <span className="phone-prefix">{form.parentMobileCountryCode}</span>
                    <input
                      id="whatsappMobile"
                      inputMode="numeric"
                      placeholder="9876543210"
                      value={form.whatsappMobile}
                      onChange={(event) => updateForm({
                        whatsappMobile: event.target.value.replace(/\D/g, '').slice(0, 10),
                      })}
                      onBlur={blurAutosave}
                      pattern="[6-9][0-9]{9}"
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </section>

          <section className="form-section">
            <div className="form-section-header">
              <h2>Declaration</h2>
              <p>
                {declarationAccepted
                  ? 'You have accepted the parent/legal guardian consent declaration.'
                  : 'You must read and accept the declaration before continuing to payment.'}
              </p>
            </div>
            <div className="btn-row">
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => setDeclarationOpen(true)}
              >
                {declarationAccepted ? 'Review declaration again' : 'Read and accept declaration'}
              </button>
              {declarationAccepted ? <span className="badge badge-success">Accepted</span> : null}
            </div>
          </section>
        </form>
      </div>

      {/* Outside the card and sticky, so the primary action is never a scroll
          away on a form this long. The `form` attribute keeps the button wired
          to the form it submits. */}
      <div className="form-actions">
        <span className="autosave-flag">
          <span className="autosave-dot" aria-hidden="true" />
          Saved automatically
        </span>
        <button className="btn btn-lg" type="submit" form="registration-form" disabled={busy}>
          {busy ? 'Submitting…' : 'Continue to registration fee'}
        </button>
      </div>

      <DeclarationModal
        open={declarationOpen}
        onOpenChange={setDeclarationOpen}
        template={declarationQuery.data}
        accepted={declarationAccepted}
        onAccept={() => setDeclarationAccepted(true)}
      />
      </div>
    </ParentShell>
  );
}
